import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import {
  FidcComparatorClassSeriesItem,
  getFidcComparatorClassSeriesOptions,
} from 'src/services/fidcComparator';

import { SelectedFund } from './types';
import { formatBaseMonthLabel } from '../dateUtils';

interface ClassSeriesSelectorProps {
  funds: SelectedFund[];
  referenceDate: string;
  onSelectionsChange: (selections: Record<string, string[]>) => void;
  noDataFunds?: string[];
}

export function ClassSeriesSelector({
  funds,
  referenceDate,
  onSelectionsChange,
  noDataFunds = [],
}: ClassSeriesSelectorProps) {
  const operationIds = useMemo(() => funds.map((f) => f.operationId), [funds]);
  const operationIdsKey = operationIds.map(String).join(',');

  const { data, isLoading, isError } = useQuery<
    FidcComparatorClassSeriesItem[]
  >(
    ['fidc-class-series-options', operationIdsKey, referenceDate],
    () => getFidcComparatorClassSeriesOptions(operationIds, referenceDate),
    {
      enabled: operationIds.length > 0 && Boolean(referenceDate),
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const [selections, setSelections] = useState<Record<string, string[]>>({});

  useEffect(() => {
    setSelections({});
  }, [referenceDate]);

  useEffect(() => {
    if (!data) return;
    setSelections((prev) => {
      const next = { ...prev };
      for (const item of data) {
        if (!next[item.operationId] && item.classSeries.length > 0) {
          next[item.operationId] = [item.classSeries[0]];
        }
      }
      return next;
    });
  }, [data]);

  const stableOnSelectionsChange = useCallback(onSelectionsChange, [
    onSelectionsChange,
  ]);
  useEffect(() => {
    stableOnSelectionsChange(selections);
  }, [selections, stableOnSelectionsChange]);

  const getOptionsForFund = (operationId: number | string): string[] =>
    data?.find((d) => String(d.operationId) === String(operationId))
      ?.classSeries ?? [];

  const emptyOptionFunds =
    data && !isLoading
      ? funds
          .filter((f) => getOptionsForFund(f.operationId).length === 0)
          .map((f) => f.name)
      : [];

  const allNoData = [...emptyOptionFunds, ...noDataFunds];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
      <Stack gap={1.5}>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Typography variant="subtitle2" fontWeight={700}>
            Classe/série por fundo
          </Typography>
          {isLoading && <CircularProgress size={14} thickness={4} />}
          <Box flex={1} />
          {referenceDate && (
            <Typography variant="caption" color="text.secondary">
              Competência:{' '}
              <Box component="span" fontWeight={600} color="text.primary">
                {formatBaseMonthLabel(referenceDate)}
              </Box>
            </Typography>
          )}
        </Stack>

        {isError && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
            Erro ao carregar classes/séries disponíveis.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}>
          {funds.map((fund) => {
            const fundKey = String(fund.operationId);
            const classSeries = getOptionsForFund(fund.operationId);
            const hasOptions = classSeries.length > 0;

            return (
              <Stack key={fundKey} gap={0.75} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {fund.name}
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  disabled={isLoading || !hasOptions}
                  loading={isLoading}
                  options={classSeries}
                  value={hasOptions ? selections[fundKey] ?? [] : []}
                  onChange={(_, nextValue) =>
                    setSelections((prev) => ({ ...prev, [fundKey]: nextValue }))
                  }
                  noOptionsText="Sem classe/série disponível"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Classe/série"
                      placeholder={
                        (selections[fundKey]?.length ?? 0) === 0
                          ? 'Selecionar'
                          : undefined
                      }
                      size="small"
                    />
                  )}
                  size="small"
                />
              </Stack>
            );
          })}
        </Box>

        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
          A seleção de classe/série será usada nas tabelas e gráficos de
          rentabilidade.
        </Alert>

        {allNoData.length > 0 && (
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Os fundos abaixo não possuem dados de rentabilidade na competência
            selecionada:
            <Box mt={0.5}>
              {allNoData.map((label) => (
                <Typography key={label} variant="caption" display="block">
                  •{' '}
                  <Box component="span" fontWeight={700}>
                    {label}
                  </Box>
                </Typography>
              ))}
            </Box>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
